import React, { type ReactElement, type ReactNode } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import ReactSelect, {
  components as ReactSelectComponents,
  type ControlProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type InputProps,
  type MenuListProps,
  type OptionProps,
  type PlaceholderProps,
  type Props as ReactSelectProps,
  type SelectComponentsConfig,
  type StylesConfig,
  type ValueContainerProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";

import { useTranslate } from "hooks";
import type { SelectOption as SharedSelectOption } from "interfaces/shared.types";
import styles from "./Select.module.scss";

type SelectOptionBase = Omit<SharedSelectOption, "label" | "value"> & {
  label?: ReactNode;
  value?: string | number;
  disabled?: boolean;
};

type BaseSelectProps<OptionType extends SelectOptionBase> = ReactSelectProps<
  OptionType,
  false,
  GroupBase<OptionType>
>;

type CreatableSelectProps<OptionType extends SelectOptionBase> = {
  formatCreateLabel?: (inputValue: string) => ReactNode;
  isValidNewOption?: (inputValue: string, ...args: unknown[]) => boolean;
  getNewOptionData?: (inputValue: string, optionLabel: ReactNode) => OptionType;
  onCreateOption?: (inputValue: string) => void;
};

type SelectProps<OptionType extends SelectOptionBase> = {
  testId?: string | null;
  isCreatable?: boolean;
  wrapperClassNames?: string;
  disabled?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
} & Omit<BaseSelectProps<OptionType>, "isDisabled"> &
  CreatableSelectProps<OptionType>;

type SelectComponentType = <OptionType extends SelectOptionBase>(
  props: SelectProps<OptionType>,
) => ReactElement | null;

const SelectComponent: SelectComponentType = <
  OptionType extends SelectOptionBase,
>({
  testId = null,
  isCreatable = false,
  wrapperClassNames,
  ...props
}: SelectProps<OptionType>) => {
  const translate = useTranslate();

  const dropdownStyles: StylesConfig<
    OptionType,
    false,
    GroupBase<OptionType>
  > = {
    option: (optionStyles, { data }) => ({
      ...optionStyles,
      alignItems: "center",
      display: "flex",
      cursor: "pointer",
      wordBreak: "break-word",
      minHeight: 30,
      ":before": {
        backgroundColor: data.color,
        borderRadius: 10,
        border: data.color ? "1px solid white" : undefined,
        content: '" "',
        display: "block",
        marginRight: data.color ? 8 : 0,
        height: data.color ? "0.75rem" : 0,
        width: data.color ? "0.75rem" : 0,
      },
    }),
    singleValue: (optionStyles, { data }) => ({
      ...optionStyles,
      alignItems: "center",
      display: "flex",
      whiteSpace: "normal",
      wordBreak: "break-word",
      textOverflow: "unset",
      ":before": {
        backgroundColor: data.color,
        borderRadius: 10,
        border: data.color ? "1px solid white" : undefined,
        content: '" "',
        display: "block",
        marginRight: data.color ? 8 : 0,
        height: data.color ? "0.75rem" : 0,
        width: data.color ? "0.75rem" : 0,
      },
    }),
    control: (controlStyles) => ({
      ...controlStyles,
      cursor: "pointer",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const Control = (
    controlProps: ControlProps<OptionType, false, GroupBase<OptionType>>,
  ) => (
    <ReactSelectComponents.Control
      {...controlProps}
      className={cx(styles.wrapper, "rounded-1", {
        "border-info": controlProps.isFocused,
        "border-danger": props.isInvalid,
      })}
    >
      {controlProps.children}
    </ReactSelectComponents.Control>
  );

  const ValueContainer = (
    valueContainerProps: ValueContainerProps<
      OptionType,
      false,
      GroupBase<OptionType>
    >,
  ) => (
    <ReactSelectComponents.ValueContainer
      {...valueContainerProps}
      className="py-0"
    >
      {valueContainerProps.children}
    </ReactSelectComponents.ValueContainer>
  );

  const Input = (
    inputProps: InputProps<OptionType, false, GroupBase<OptionType>>,
  ) => <ReactSelectComponents.Input {...inputProps} />;

  const DropdownIndicator = (
    dropdownIndProps: DropdownIndicatorProps<
      OptionType,
      false,
      GroupBase<OptionType>
    >,
  ) => (
    <ReactSelectComponents.DropdownIndicator
      {...dropdownIndProps}
      className={cx("py-0", {
        "rotate-180": dropdownIndProps.selectProps.menuIsOpen,
      })}
    />
  );

  const OptionRenderer = (
    optionProps: OptionProps<OptionType, false, GroupBase<OptionType>>,
  ) => (
    <ReactSelectComponents.Option
      {...optionProps}
      className={cx({
        "bg-blue-300 text-white": optionProps.isFocused,
        "bg-blue-400 text-white": optionProps.isSelected,
      })}
      isDisabled={optionProps.isDisabled || optionProps.data?.disabled || false}
    >
      <div
        data-testid={
          typeof optionProps.data.value === "string"
            ? optionProps.data.value
            : undefined
        }
      >
        {optionProps.children}
      </div>
    </ReactSelectComponents.Option>
  );

  const MenuList = (
    menuListProps: MenuListProps<OptionType, false, GroupBase<OptionType>>,
  ) => (
    <ReactSelectComponents.MenuList
      {...menuListProps}
      className={cx("p-0", styles.menuList)}
    >
      {menuListProps.children}
    </ReactSelectComponents.MenuList>
  );

  const placeholderText = translate("SELECT");

  const Placeholder = (
    placeholderProps: PlaceholderProps<
      OptionType,
      false,
      GroupBase<OptionType>
    >,
  ) => (
    <ReactSelectComponents.Placeholder {...placeholderProps}>
      {placeholderText}
    </ReactSelectComponents.Placeholder>
  );

  const components: SelectComponentsConfig<
    OptionType,
    false,
    GroupBase<OptionType>
  > = {
    Control,
    ValueContainer,
    DropdownIndicator,
    Option: OptionRenderer,
    MenuList,
    Input,
    Placeholder,
  };

  const isDisabled = Boolean(props.disabled || props.isDisabled);

  if (isCreatable) {
    return (
      <div
        data-testid={testId ?? undefined}
        id="select-placeholder"
        className="w-100"
      >
        <CreatableSelect
          minMenuHeight={200}
          menuPosition="absolute"
          menuPlacement="auto"
          components={components}
          styles={dropdownStyles}
          classNamePrefix={testId ?? undefined}
          isDisabled={isDisabled}
          {...props}
        />
      </div>
    );
  }

  return (
    <div
      data-testid={testId ?? undefined}
      id="select-placeholder"
      className={wrapperClassNames}
    >
      <ReactSelect
        minMenuHeight={200}
        menuPosition="absolute"
        components={components}
        styles={dropdownStyles}
        classNamePrefix={testId ?? undefined}
        isDisabled={isDisabled}
        {...props}
      />
    </div>
  );
};

export const Select = Scrivito.connect(SelectComponent) as SelectComponentType;
